import {
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAgGridCommon } from 'common/table/agGrid/AgGridCommon';
import { useAppDispatch } from 'hooks/useAppDispatch';
import React, { forwardRef, useCallback } from 'react';
import { fetchCdfUnitFundingAmountPromise } from 'reducers/cdfUnitFundingAmountReducer';
import { Header, Segment } from 'semantic-ui-react';
import { FetchCdfUnitFundingAmountResponse } from 'ts/api/cdf_unit_funding_amount';
import {
  commonPaginationOptions,
  externallyFilteredColDef,
  mergeGridOpts,
} from 'utils/agGrid/options';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import columnDefs from './columnDefs';
import { processSSRM } from 'utils/agGrid/ssrm/processSSRM';
import useExternalFilter from 'utils/agGrid/hooks/useExternalFilter';
import FundingAmount from 'ts/models/FundingAmount.model';
import ExternalFilters from './ExternalFilters';

const options = mergeGridOpts(commonPaginationOptions, {
  defaultColDef: externallyFilteredColDef,
});

export const FUNDING_RATES_GRID_NAME = 'fundingRates';

const FundingRatesGrid = forwardRef<AgGridReact>((_, ref) => {
  const dispatch = useAppDispatch();
  const { gridVisible, initialGridSettings, onStateUpdated } = useAgGridCommon({
    gridName: FUNDING_RATES_GRID_NAME,
  });

  const fetchData = useCallback(
    async (params: IServerSideGetRowsParams) => {
      const { request } = params;
      const body = parseAGRequest(request, { transformAttrs: true });
      const response: FetchCdfUnitFundingAmountResponse =
        await fetchCdfUnitFundingAmountPromise(body, dispatch);
      return {
        rows: response?.fundingAmounts,
        count: response.count,
      };
    },
    [dispatch],
  );

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => processSSRM(params, fetchData),
    [fetchData],
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      const dataSource: IServerSideDatasource = {
        getRows,
      };
      params.api?.setGridOption('serverSideDatasource', dataSource);
    },
    [getRows],
  );

  const externalFilterProps = useExternalFilter<FundingAmount>({
    gridRef: ref as React.RefObject<AgGridReact>,
    options: commonPaginationOptions,
  });

  return (
    <Segment>
      <Header as="h3" icon textAlign="left">
        Funding Rates
      </Header>
      <ExternalFilters {...externalFilterProps} />
      <div className="ag-theme-quartz">
        {gridVisible && (
          <AgGridReact
            ref={ref}
            columnDefs={columnDefs}
            gridOptions={options}
            onStateUpdated={onStateUpdated}
            initialState={initialGridSettings}
            rowModelType="serverSide"
            onGridReady={onGridReady}
          />
        )}
      </div>
    </Segment>
  );
});
FundingRatesGrid.displayName = 'FundingRatesGrid';

export default FundingRatesGrid;
